import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Check, X, FileText, MessageSquare } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

/**
 * /agent/review — Todd's review desk for agent work.
 * Two queues: pending approvals (gate-B requests) and message drafts
 * (draft-only: Todd taps send later — send itself stays manual in Phase 1).
 */
export default function AgentReview() {
    const [approvals, setApprovals] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [err, setErr] = useState("");
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const [a, d] = await Promise.all([
                api.get("/agent/v1/actions/approvals"),
                api.get("/agent/v1/actions/message-drafts"),
            ]);
            setApprovals(a.data.approvals || []);
            setDrafts(d.data.drafts || []);
            setErr("");
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const decide = async (id, decision) => {
        setBusyId(id);
        try {
            await api.post(`/agent/v1/actions/approvals/${id}/decision`, { decision });
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="page-shell">
            <header className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="eyebrow">Agent review desk</p>
                    <h1 className="h1 mt-3">Approvals &amp; drafts</h1>
                    <p className="text-muted mt-2 max-w-[680px]">
                        Everything the agent wants to do that touches money or
                        customers lands here first. Approve an action to mint the
                        agent's one-time token; message drafts stay draft-only —
                        you tap send after checking them.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={load}
                    data-testid="review-refresh"
                >
                    <RefreshCw size={16} /> Refresh
                </button>
            </header>

            {err && <p className="text-red mt-3">{err}</p>}

            <section className="mt-8">
                <h2 className="h3">
                    Pending approvals{" "}
                    <span className="count-pill">{approvals.length}</span>
                </h2>
                {approvals.length === 0 && (
                    <p className="text-muted mt-2">Nothing waiting on you.</p>
                )}
                <div className="flex flex-col gap-3 mt-4">
                    {approvals.map((a) => (
                        <div
                            key={a.id}
                            className="brand-card review-card"
                            data-testid="approval-card"
                        >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="h3 flex items-center gap-2">
                                        {a.action === "estimate_draft" ? (
                                            <FileText size={16} />
                                        ) : (
                                            <MessageSquare size={16} />
                                        )}
                                        {a.action}{" "}
                                        <span className="gate-badge">
                                            gate-{a.action_class}
                                        </span>
                                    </p>
                                    <p className="text-muted text-sm mt-1">
                                        {a.note || "No note"} · requested{" "}
                                        {a.created_at ? fmtDateTime(a.created_at) : "—"}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        className="btn btn-primary"
                                        disabled={busyId === a.id}
                                        onClick={() => decide(a.id, "approved")}
                                        data-testid={`approve-${a.id}`}
                                    >
                                        <Check size={16} /> Approve
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        disabled={busyId === a.id}
                                        onClick={() => decide(a.id, "rejected")}
                                    >
                                        <X size={16} /> Reject
                                    </button>
                                </div>
                            </div>
                            {a.payload && (
                                <details className="review-payload">
                                    <summary className="text-muted text-sm">
                                        Payload
                                    </summary>
                                    <pre>{JSON.stringify(a.payload, null, 2)}</pre>
                                </details>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            <section className="mt-10">
                <h2 className="h3">
                    Message drafts{" "}
                    <span className="count-pill">{drafts.length}</span>
                </h2>
                <p className="text-muted text-sm mt-1">
                    Draft-only in Phase 1 — open your email client and send after
                    checking. One-tap send ships in Phase 2 after a clean record.
                </p>
                {drafts.length === 0 && (
                    <p className="text-muted mt-2">No drafts yet.</p>
                )}
                <div className="flex flex-col gap-3 mt-4">
                    {drafts.map((d) => (
                        <div key={d.id} className="brand-card review-card">
                            <p className="h3">{d.subject}</p>
                            <p className="text-muted text-sm mt-1">
                                To {d.to_email} · {d.created_at ? fmtDateTime(d.created_at) : "—"}{" "}
                                · status: {d.status}
                            </p>
                            <pre className="review-body">{d.body}</pre>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}